<template>
    <div v-if="lastPage > 1"
        class="flex flex-col sm:flex-row justify-between items-center gap-4 py-4 px-4 border-t border-gray-200">
        <!-- Info de pagination -->
        <div class="text-sm text-gray-600">
            Affichage de <span class="font-medium">{{ fromValue }}</span> à <span class="font-medium">{{ to }}</span> sur
            <span class="font-medium">{{ total }}</span> éléments
        </div>

        <!-- Contrôles de pagination -->
        <div class="flex items-center gap-3">
            <!-- Bouton précédent -->
            <BaseButton variant="ghost" size="sm" @click="onPageChange(currentPage - 1)" :disabled="currentPage === 1">
                <template #icon-left>
                    <i class="fa-solid fa-chevron-left"></i>
                </template>
                Précédent
            </BaseButton>

            <!-- Numéros de page -->
            <div v-if="showPageNumbers" class="flex items-center gap-1">
                <button v-for="page in displayedPages" :key="page" @click="onPageChange(page)" :class="[
                    'w-9 h-9 rounded-lg text-sm font-medium transition-all duration-200',
                    page === currentPage
                        ? 'bg-primary text-white shadow-sm'
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                ]">
                    {{ page }}
                </button>
            </div>

            <!-- Bouton suivant -->
            <BaseButton variant="ghost" size="sm" @click="onPageChange(currentPage + 1)"
                :disabled="currentPage >= lastPage">
                Suivant
                <i class="fa-solid fa-chevron-right ml-2"></i>
            </BaseButton>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue'
import BaseButton from '@/components/base/BaseButton.vue'

export default {
    name: 'PaginationComponent',
    components: {
        BaseButton
    },
    props: {
        currentPage: {
            type: Number,
            required: true
        },
        perPage: {
            type: Number,
            required: true
        },
        total: {
            type: Number,
            required: true
        },
        to: {
            type: Number,
            required: true
        },
        from: {
            type: Number,
            default: null
        },
        showPageNumbers: {
            type: Boolean,
            default: true
        },
        maxVisiblePages: {
            type: Number,
            default: 5
        }
    },
    emits: ['page-change'],
    setup(props, { emit }) {
        const lastPage = computed(() => {
            return Math.ceil(props.total / props.perPage)
        })

        const fromValue = computed(() => {
            // Si `from` est fourni en prop (format backend), l'utiliser
            if (props.from !== null && props.from !== undefined) {
                return props.from
            }
            // Sinon, calculer à partir de currentPage et perPage
            if (props.total === 0) return 0
            return (props.currentPage - 1) * props.perPage + 1
        })

        const displayedPages = computed(() => {
            const pages = []
            const last = lastPage.value
            const current = props.currentPage
            const max = props.maxVisiblePages

            if (last <= max) {
                // Si le nombre total de pages est inférieur ou égal au maximum de pages visibles,
                // afficher toutes les pages
                for (let i = 1; i <= last; i++) {
                    pages.push(i)
                }
            } else {
                // Sinon, afficher intelligemment les pages autour de la page courante
                if (current <= Math.ceil(max / 2)) {
                    // Près du début
                    for (let i = 1; i <= max; i++) {
                        pages.push(i)
                    }
                } else if (current >= last - Math.floor(max / 2)) {
                    // Près de la fin
                    for (let i = last - max + 1; i <= last; i++) {
                        pages.push(i)
                    }
                } else {
                    // Au milieu
                    const half = Math.floor(max / 2)
                    for (let i = current - half; i <= current + half; i++) {
                        if (i > 0 && i <= last) {
                            pages.push(i)
                        }
                    }

                    // Ajuster si nécessaire pour avoir exactement maxVisiblePages
                    while (pages.length < max) {
                        if (pages[0] > 1) {
                            pages.unshift(pages[0] - 1)
                        } else if (pages[pages.length - 1] < last) {
                            pages.push(pages[pages.length - 1] + 1)
                        } else {
                            break
                        }
                    }
                }
            }

            return pages
        })

        const onPageChange = (page) => {
            if (page < 1 || page > lastPage.value) return
            emit('page-change', page)
        }

        return {
            lastPage,
            fromValue,
            displayedPages,
            onPageChange
        }
    }
}
</script>